// src/components/Tickets/QuotationCard.tsx
// VERSIÓN MEJORADA con regeneración completa

import { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../hooks/useToast';
import { 
  FileText, DollarSign, Clock, Calendar, 
  CheckCircle, XCircle, Phone, TrendingUp, Zap,
  Download, RefreshCw
} from 'lucide-react';
import { buildApiUrl } from '../../config/api';
import TicketScoreModal from './TicketScoreModal'; // 👈 AGREGAR ESTE IMPORT

interface QuotationCardProps {
  quotation: any;
  onUpdate: () => void;
}

export default function QuotationCard({ quotation, onUpdate }: QuotationCardProps) {
  const { isDark } = useTheme();
  const { success, error: showError } = useToast();
  const [downloading, setDownloading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  
  // 👇 NUEVOS ESTADOS PARA MODAL DE REGENERACIÓN
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [previousScoreData, setPreviousScoreData] = useState<any>(null);

  // Función para descargar PDF
  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      console.log('[QUOTATION] Descargando PDF para:', quotation.ticket_id);

      const token = localStorage.getItem('kikai_token') || localStorage.getItem('token');
      const response = await fetch(
        buildApiUrl(`/tickets/${quotation.ticket_id}/quotation/download`),
        {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('[QUOTATION] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        console.error('[QUOTATION] Error response:', errorData);
        throw new Error(errorData.error || 'Error descargando PDF');
      }

      // Crear blob y descargar
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quotation.quotation_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      success('PDF descargado exitosamente');
      console.log('[QUOTATION] PDF descargado');

    } catch (err: any) {
      console.error('[QUOTATION] Error descargando:', err);
      showError(err.message || 'Error al descargar el PDF');
    } finally {
      setDownloading(false);
    }
  };

  // ==========================================
  // 👇 FUNCIÓN MEJORADA: handleRegenerate
  // ==========================================
  const handleRegenerate = async () => {
    try {
      setRegenerating(true);
      console.log('[QUOTATION-REGEN] Obteniendo datos de evaluación previa para:', quotation.ticket_id);

      const token = localStorage.getItem('kikai_token') || localStorage.getItem('token');
      
      // Obtener datos del score anterior
      const response = await fetch(
        buildApiUrl(`/tickets/${quotation.ticket_id}/score`),
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'No se pudo obtener evaluación anterior' }));
        throw new Error(errorData.error || 'No se encontró evaluación previa');
      }

      const scoreData = await response.json();
      console.log('[QUOTATION-REGEN] Datos obtenidos:', scoreData);

      // Guardar datos y abrir modal
      setPreviousScoreData(scoreData);
      setShowRegenerateModal(true);

      success('✏️ Modal de re-evaluación abierto. Edita los valores necesarios.');

    } catch (err: any) {
      console.error('[QUOTATION-REGEN] Error:', err);
      showError(err.message || 'Error al cargar datos de evaluación. Asegúrate de que el ticket fue evaluado previamente.');
    } finally {
      setRegenerating(false);
    }
  };

  // ==========================================
  // 👇 NUEVA FUNCIÓN: handleRegenerateSubmit
  // ==========================================
  const handleRegenerateSubmit = async (newScoreData: any) => {
    try {
      console.log('[QUOTATION-REGEN] Regenerando con nuevos datos:', newScoreData);

      const token = localStorage.getItem('kikai_token') || localStorage.getItem('token');
      
      // 1. Marcar cotización anterior como rechazada
      const rejectResponse = await fetch(
        buildApiUrl(`/tickets/${quotation.ticket_id}/quote/regenerate`),
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!rejectResponse.ok) {
        throw new Error('Error al marcar cotización anterior como rechazada');
      }

      console.log('[QUOTATION-REGEN] Cotización anterior marcada como rechazada');

      // 2. Re-evaluar ticket con nuevos datos
      const scoreResponse = await fetch(
        buildApiUrl(`/tickets/${quotation.ticket_id}/score`),
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(newScoreData)
        }
      );

      if (!scoreResponse.ok) {
        const errorData = await scoreResponse.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || 'Error al re-evaluar ticket');
      }

      const result = await scoreResponse.json();
      console.log('[QUOTATION-REGEN] Re-evaluación exitosa:', result);

      success('✅ Cotización regenerada exitosamente');

      // Cerrar modal y limpiar
      setShowRegenerateModal(false);
      setPreviousScoreData(null);
      
      // Actualizar vista
      setTimeout(() => {
        onUpdate();
      }, 500);

    } catch (err: any) {
      console.error('[QUOTATION-REGEN] Error:', err);
      showError(err.message || 'Error al regenerar cotización');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500';
      case 'approved': return 'bg-green-500';
      case 'rejected': return 'bg-red-500';
      case 'paid': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'approved': return 'Aprobada';
      case 'rejected': return 'Rechazada';
      case 'paid': return 'Pagada';
      default: return status;
    }
  };

  // Parsear el breakdown si está en la descripción
  const parseBreakdown = (description: string) => {
    const lines = description.split('\n');
    const breakdown: any = {};
    
    lines.forEach(line => {
      if (line.includes('Complejidad técnica') || line.includes('Complejidad tecnica')) {
        const match = line.match(/\$([0-9,.]+)/);
        if (match) breakdown.complexityPrice = parseFloat(match[1].replace(',', ''));
      }
      if (line.includes('Urgencia')) {
        const match = line.match(/\$([0-9,.]+)/);
        if (match) breakdown.urgencyPrice = parseFloat(match[1].replace(',', ''));
      }
      if (line.includes('Horas estimadas')) {
        const hoursMatch = line.match(/\(([0-9.]+)h\)/);
        const priceMatch = line.match(/\$([0-9,.]+)/);
        if (hoursMatch) breakdown.estimated_hours = parseFloat(hoursMatch[1]);
        if (priceMatch) breakdown.hoursPrice = parseFloat(priceMatch[1].replace(',', ''));
      }
      if (line.includes('Solución específica') || line.includes('Solucion especifica')) {
        const hoursMatch = line.match(/\(([0-9.]+)h\)/);
        const priceMatch = line.match(/\$([0-9,.]+)/);
        if (hoursMatch) breakdown.specific_solution_hours = parseFloat(hoursMatch[1]);
        if (priceMatch) breakdown.specificSolutionPrice = parseFloat(priceMatch[1].replace(',', ''));
      }
      if (line.includes('Subtotal')) {
        const match = line.match(/\$([0-9,.]+)/);
        if (match) breakdown.subtotal = parseFloat(match[1].replace(',', ''));
      }
    });
    
    return breakdown;
  };

  const breakdown = parseBreakdown(quotation.description || '');

  return (
    <>
      <div className={`${
        isDark ? 'bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-purple-700' : 'bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200'
      } border rounded-xl p-6 mb-6`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-6 h-6 text-purple-500" />
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Cotización Generada
              </h3>
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {quotation.quotation_number}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <span className={`${getStatusColor(quotation.status)} text-white px-4 py-2 rounded-lg text-sm font-medium`}>
              {getStatusLabel(quotation.status)}
            </span>
          </div>
        </div>

        {/* Desglose de Precios */}
        <div className={`${
          isDark ? 'bg-gray-800/50' : 'bg-white/80'
        } rounded-xl p-5 mb-4 space-y-3`}>
          <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-3 flex items-center gap-2`}>
            <DollarSign className="w-4 h-4" />
            Desglose de Costos
          </h4>

          {breakdown.complexityPrice > 0 && (
            <div className="flex justify-between items-center">
              <span className={`text-sm flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <Zap className="w-4 h-4 text-blue-500" />
                Complejidad Técnica
              </span>
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                ${breakdown.complexityPrice.toLocaleString('es-CL')}
              </span>
            </div>
          )}

          {breakdown.urgencyPrice > 0 && (
            <div className="flex justify-between items-center">
              <span className={`text-sm flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <TrendingUp className="w-4 h-4 text-orange-500" />
                Nivel de Urgencia
              </span>
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                ${breakdown.urgencyPrice.toLocaleString('es-CL')}
              </span>
            </div>
          )}

          {breakdown.estimated_hours > 0 && (
            <div className="flex justify-between items-center">
              <span className={`text-sm flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <Clock className="w-4 h-4 text-green-500" />
                Trabajo Presencial ({breakdown.estimated_hours}h)
              </span>
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                ${breakdown.hoursPrice?.toLocaleString('es-CL') || '0'}
              </span>
            </div>
          )}

          {breakdown.specific_solution_hours > 0 && (
            <div className="flex justify-between items-center">
              <span className={`text-sm flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <Phone className="w-4 h-4 text-purple-500" />
                Solución Específica ({breakdown.specific_solution_hours}h)
              </span>
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                ${breakdown.specificSolutionPrice?.toLocaleString('es-CL') || '0'}
              </span>
            </div>
          )}

          <div className={`pt-3 mt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
            <div className="flex justify-between items-center">
              <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                TOTAL
              </span>
              <span className="text-2xl font-bold text-purple-600">
                ${quotation.amount?.toLocaleString('es-CL') || '0'}
              </span>
            </div>
          </div>
        </div>

        {/* Fecha y Acciones */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            Generada: {new Date(quotation.created_at).toLocaleDateString('es-CL', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>

          {/* Botones de Acción */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                downloading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              <Download className={`w-4 h-4 ${downloading ? 'animate-bounce' : ''}`} />
              {downloading ? 'Descargando...' : 'Descargar PDF'}
            </button>

            {quotation.status === 'pending' && (
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  regenerating
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-orange-600 hover:bg-orange-700 text-white'
                }`}
                title="Abrir modal para editar evaluación y regenerar cotización"
              >
                <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? 'Cargando...' : 'Regenerar'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ==========================================
          👇 MODAL DE REGENERACIÓN
          ========================================== */}
      {showRegenerateModal && (
        <TicketScoreModal
          ticketId={quotation.ticket_id}
          ticketTitle={`Regenerar: ${quotation.ticket?.title || 'Ticket'}`}
          initialData={previousScoreData}
          onClose={() => {
            setShowRegenerateModal(false);
            setPreviousScoreData(null);
          }}
          onSuccess={handleRegenerateSubmit}
          isRegenerating={true}
        />
      )}
    </>
  );
}